import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import type { Field } from 'factorial-form'
import isEmpty from 'lodash/isEmpty'

import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import employees from 'modules/employees/store/collections/employees'
import MoveEmployeeForm from 'modules/locations/lib/forms/MoveEmployeeForm'
import locations from 'modules/locations/store/collections/locations'

interface Props {
  locationId: number
  employeeId: number
  onClose: () => void
}

const MoveEmployee = ({ employeeId, locationId, onClose }: Props) => {
  const [locationSelected, setlocationSelected] = React.useState<boolean>(false)

  const locationForm = useLazyRef(
    () =>
      new MoveEmployeeForm({
        location_id: locationId,
      })
  ).current

  const location = useMemo(() => {
    const location = locations.get(locationId)

    if (!location) {
      throw Error(`Error getting locations with id = ${locationId}`)
    }

    return location
  }, [locationId])

  const employee = useMemo(() => {
    const employee = employees.get(employeeId)

    if (!employee) {
      throw Error(`Error getting employees with id = ${employeeId}`)
    }

    return employee
  }, [employeeId])

  const options = useMemo(() => {
    return locations.models
      .filter((top) => top.attributes.id !== location.attributes.id)
      .map((top) => ({
        label: top.attributes.name,
        value: top.attributes.id,
      }))
  }, [location.attributes.id])

  const onSelect = (field: Field) => {
    setlocationSelected(!isEmpty(field.value))
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const locationId = locationForm.get('location_id').value
    await employee.save({
      location_id: locationId,
    })
    closePortal()
  }

  return (
    <Modal
      illustration={<UserAvatar user={employeeUser(employee)} size="s80" />}
      title={i18n.t('settings.locations.move_employee_modal.title', {
        employee: employeeUser(employee).fullName(),
      })}
      description={i18n.t('settings.locations.move_employee_modal.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={!locationSelected} submit>
          {i18n.t('common.submit')}
        </Button>
      }
    >
      <Select
        none={i18n.t('common.none')}
        field={locationForm.get('location_id')}
        label={i18n.t('settings.locations.title')}
        onChange={(field) => onSelect(field)}
        options={options}
      />
    </Modal>
  )
}

export default MoveEmployee
